import React, { useRef, useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// ソート可能なリストアイテムコンポーネント
const SortableItem = ({ elevator, onSelect, onEdit, onDelete, onInfo }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: elevator.regNumber });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-2 ux-rounded transition-all ${isDragging ? 'bg-blue-50 shadow-lg z-50' : 'hover:bg-gray-50'
        }`}
    >
      <div className="flex items-center elevator-main flex-1 min-w-0">
        {/* ドラッグハンドル */}
        <div
          {...attributes}
          {...listeners}
          className="mr-2 cursor-grab active:cursor-grabbing"
          style={{
            touchAction: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}
        >
          <img
            src="./assets/static/MEL_2.png"
            className="w-6 h-6 flex-shrink-0"
            alt="並び替えアイコン"
            draggable={false}
            style={{
              pointerEvents: 'none',
              WebkitUserDrag: 'none',
              userDrag: 'none',
            }}
          />
        </div>
        <span
          className="favorite-name ml-1 text-gray-700 truncate cursor-pointer w-full"
          onClick={() => onSelect(elevator)}
        >
          {elevator.name}
        </span>
      </div>

      <div className="flex items-center space-x-1 flex-shrink-0">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(elevator);
          }}
          className="p-1 text-gray-500 hover:text-blue-600 rounded-full focus:outline-none transition-colors"
        >
          <img src="./assets/static/MEL_3.png" className="w-6 h-6" alt="編集" />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onInfo(elevator);
          }}
          className="p-1 text-gray-500 hover:text-blue-600 rounded-full focus:outline-none transition-colors"
        >
          <img src="./assets/static/MEL_5.png" className="w-6 h-6" alt="編集" />
        </button>

        <span className="text-gray-300">|</span>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(elevator);
          }}
          className="p-1 text-gray-500 hover:text-red-600 rounded-full focus:outline-none transition-colors"
        >
          <img src="./assets/static/MEL_4.png" className="w-6 h-6" alt="削除" />
        </button>
      </div>
    </li>
  );
};

const FavoritesOverlay = ({
  isOpen,
  myElevators,
  onClose,
  onSelectElevator,
  onEditElevator,
  onDeleteElevator,
  onReorderElevators,
  onElevatorInfo,
}) => {
  const cardRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [elevators, setElevators] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 500, // 長めに設定
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setElevators(myElevators);
  }, [myElevators]);

  useEffect(() => {
    if (isOpen && cardRef.current) {
      const header = document.querySelector('header');
      if (header) {
        const inputContainer = header.querySelector('div[class*="rounded"]');
        if (inputContainer) {
          const inputRect = inputContainer.getBoundingClientRect();
          cardRef.current.style.top = `${inputRect.top + inputRect.height / 2}px`;
          cardRef.current.style.borderTopLeftRadius = inputContainer.style.borderTopLeftRadius;
          cardRef.current.style.borderTopRightRadius = inputContainer.style.borderTopRightRadius;
        }
      }

      requestAnimationFrame(() => {
        cardRef.current.classList.remove('opacity-0', '-translate-y-4');
        cardRef.current.classList.add('opacity-100', 'translate-y-0');
      });
    }
  }, [isOpen]);

  // LINEブラウザの下スワイプジェスチャーを防ぐ
  useEffect(() => {
    if (!isOpen) return;

    const preventPullToRefresh = (e) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const scrollContainer = scrollContainerRef.current;
    const card = cardRef.current;

    if (scrollContainer) {
      scrollContainer.addEventListener('touchmove', preventPullToRefresh, { passive: false });
    }
    if (card) {
      card.addEventListener('touchmove', preventPullToRefresh, { passive: false });
    }

    // ページ全体のスクロールを一時的に無効化（モーダル表示中のみ）
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('touchmove', preventPullToRefresh);
      }
      if (card) {
        card.removeEventListener('touchmove', preventPullToRefresh);
      }

      // ページのスクロールを復元
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen, isDragging]);

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  const handleCardClick = (e) => {
    e.stopPropagation();
  };

  const handleElevatorSelect = (elevator) => {
    onSelectElevator(elevator.regNumber);
    onClose();
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (event) => {
    setIsDragging(false);

    const { active, over } = event;

    if (over && active.id !== over.id) {
      setElevators((items) => {
        const oldIndex = items.findIndex((item) => item.regNumber === active.id);
        const newIndex = items.findIndex((item) => item.regNumber === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // 並び替え結果をAPIに送信
        const order = newItems.map((e) => e.regNumber);
        onReorderElevators(order);

        return newItems;
      });
    }
  };

  const handleDragCancel = () => {
    setIsDragging(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed bg-black bg-opacity-60 z-40"
        style={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        onClick={handleOverlayClick}
      />

      <div
        ref={cardRef}
        className="fixed bg-white rounded-b-lg shadow-xl transform transition-all duration-300 ease-out opacity-0 -translate-y-4 z-40"
        style={{
          width: 'calc(100vw/12*10)',
          left: 'calc(100vw/12)',
          touchAction: isDragging ? 'none' : 'pan-y',
          WebkitOverflowScrolling: 'touch',
        }}
        onClick={handleCardClick}
      >
        <div className="h-[calc(100svh/72*2)] opacity-0" />

        <div className="px-4 pt-4 pb-2 border-b">
          <h3 className="text-xs text-gray-500 ml-1 text-start">マイエレベータ一覧</h3>
        </div>

        <div
          ref={scrollContainerRef}
          className="overflow-y-auto"
          style={{
            maxHeight: 'calc(5 * 3.5rem)',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            modifiers={[restrictToVerticalAxis]}
            autoScroll={false}
          >
            <SortableContext
              items={elevators.map((e) => e.regNumber)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="p-2 divide-y divide-gray-200">
                {elevators.length === 0 ? (
                  <li className="p-4 text-center text-gray-500">
                    登録されたエレベータがありません
                  </li>
                ) : (
                  elevators.map((elevator) => (
                    <SortableItem
                      key={elevator.regNumber}
                      elevator={elevator}
                      onSelect={handleElevatorSelect}
                      onEdit={onEditElevator}
                      onDelete={onDeleteElevator}
                      onInfo={onElevatorInfo}
                    />
                  ))
                )}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </>
  );
};

export default FavoritesOverlay;